import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMediaQuery } from '../../hooks/use-media-query';
import TooltipContainer from '../Tooltip/TooltipContainer';
import { Popover, usePopoverRef } from '../Popover';
import { useSheetRouter } from '../SheetRouter/SheetRouterContext';

type Props = {
  title: string;
  description?: string;
  disabled?: boolean;
  children: (onOpen: () => void) => React.ReactNode;
};

const LONG_PRESS_DELAY = 500;

const Tooltip = ({ title, description, disabled, children }: Props) => {
  const { openTooltip } = useSheetRouter();
  const supportsHover = useMediaQuery('(hover: hover)');
  const popoverRef = usePopoverRef();

  const timeoutRef = useRef<number | null>(null);
  const startPosition = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (disabled) {
      clearTimeout(timeoutRef.current ?? undefined);
    }
  }, [disabled]);

  // Start the long press timer
  const handlePointerDown = (event: React.PointerEvent) => {
    if (disabled || !description) {
      return;
    }
    startPosition.current = { x: event.clientX, y: event.clientY };

    timeoutRef.current = setTimeout(() => {
      openTooltip({
        title,
        description,
      });
    }, LONG_PRESS_DELAY) as unknown as number;
  };

  // Cancel the long press if pointer moves significantly
  const handlePointerMove = (event: React.PointerEvent) => {
    const distanceMoved = Math.sqrt(
      Math.pow(event.clientX - startPosition.current.x, 2) + Math.pow(event.clientY - startPosition.current.y, 2),
    );

    // If the pointer moves more than 10 pixels, cancel the long press
    if (distanceMoved > 10) {
      clearTimeout(timeoutRef.current ?? undefined);
    }
  };

  // Clean up on pointer up or leave
  const handlePointerUpOrLeave = () => {
    clearTimeout(timeoutRef.current ?? undefined);
  };

  const handleOpenTooltip = useCallback(() => {
    if (!description) {
      return;
    }

    if (supportsHover) {
      popoverRef.current?.open();
    } else {
      openTooltip({
        title,
        description,
      });
    }
  }, [popoverRef, supportsHover, description, openTooltip]);

  if (supportsHover) {
    const longDescription = description && description.length > 40;
    return (
      <Popover
        ref={popoverRef}
        trigger="none"
        closeTrigger="hover"
        isDisabled={disabled}
        content={<TooltipContainer size={longDescription ? 'large' : 'small'}>{description}</TooltipContainer>}
        animate
        delayOnHover={300}
        placement="bottom"
      >
        {children(handleOpenTooltip)}
      </Popover>
    );
  }

  return (
    <div
      className="ui-tooltip"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUpOrLeave}
      onPointerLeave={handlePointerUpOrLeave}
    >
      {children(handleOpenTooltip)}
    </div>
  );
};

export default Tooltip;
